import React, { useEffect, useRef, useState } from "react";
import { Alert, Progress, Spinner } from "reactstrap";
import {
	getAiVisibilityTaskStatus,
	isTaskComplete,
	isTaskFailed,
	normalizeTaskStatus,
	POLL_MS,
} from "../../services/aiVisibilityApi";

const AiVisibilitySetupProgress = ({ websiteId, initialTaskStatus, onComplete, onFailed }) => {
	const initial = normalizeTaskStatus(initialTaskStatus);
	const [progress, setProgress] = useState(initial.progress);
	const [status, setStatus] = useState(initial.status);
	const [error, setError] = useState(initial.error);
	const [polling, setPolling] = useState(false);
	const completedRef = useRef(false);
	const intervalRef = useRef(null);
	const onCompleteRef = useRef(onComplete);
	const onFailedRef = useRef(onFailed);

	onCompleteRef.current = onComplete;
	onFailedRef.current = onFailed;

	useEffect(() => {
		if (!websiteId) return undefined;

		completedRef.current = false;

		const applyUpdate = (nextProgress, nextStatus, nextError) => {
			setProgress(nextProgress);
			setStatus(nextStatus);
			setError(nextError);

			if (completedRef.current) return;

			if (isTaskFailed(nextStatus, nextError)) {
				completedRef.current = true;
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
				onFailedRef.current?.(nextError);
				return;
			}

			if (isTaskComplete(nextProgress, nextStatus)) {
				completedRef.current = true;
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
				setProgress(100);
				onCompleteRef.current?.();
			}
		};

		applyUpdate(initial.progress, initial.status, initial.error);
		if (completedRef.current) return undefined;

		const tick = async () => {
			if (completedRef.current) return;
			setPolling(true);
			const result = await getAiVisibilityTaskStatus(websiteId);
			setPolling(false);
			if (!result.ok || completedRef.current) return;
			applyUpdate(result.progress, result.status, result.error);
		};

		tick();
		intervalRef.current = setInterval(tick, POLL_MS);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [websiteId, initial.progress, initial.status, initial.error]);

	const statusLabel = status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending";

	if (error && isTaskFailed(status, error)) {
		return (
			<Alert color="danger" className="mb-4">
				<strong>Setup failed.</strong> {error}
			</Alert>
		);
	}

	return (
		<div className="mb-4">
			<div className="d-flex justify-content-between align-items-center mb-2">
				<span className="fw-semibold">Visibility audit</span>
				<span className="text-muted small d-flex align-items-center gap-2">
					{polling && <Spinner size="sm" />}
					{progress}% - {statusLabel}
				</span>
			</div>
			<Progress value={progress} />
			<p className="text-muted small mb-0 mt-2">
				{progress >= 100
					? "Complete. Opening your dashboard…"
					: "Analyzing your site for AI visibility. You will be redirected when setup reaches 100%."}
			</p>
		</div>
	);
};

export default AiVisibilitySetupProgress;
